import { useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

/* GSAP helpers shared by the dashboards. Every helper is a no-op when the
   visitor asked the OS for reduced motion, and every tween is killed when
   the component that started it unmounts. */

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Layout effect in the browser (sets "from" states before paint), plain
// effect elsewhere (jsdom tests)
export const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/* Runs `setup` inside a gsap.context scoped to `scopeRef`. Everything the
   setup creates is reverted — tweens killed, inline styles removed — on
   unmount or when `deps` change. */
export function useGsap(setup, deps = [], scopeRef) {
  const setupRef = useRef(setup);
  setupRef.current = setup;
  useIsoLayoutEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(
      (self) => setupRef.current && setupRef.current(self),
      scopeRef ? scopeRef.current : undefined
    );
    return () => ctx.revert();
  }, deps);
}

/* Staggered reveal for lists that grow and shrink (filters, search).
   On every render it looks for `selector` elements it has not seen yet and
   rises them in; elements that stay mounted are never animated twice.
   Only the first `max` new elements animate — the rest appear at once. */
export function useStaggerReveal(
  scopeRef,
  { selector = '[data-reveal]', y = 16, stagger = 0.045, max = 16 } = {}
) {
  const ctxRef = useRef(null);
  const seenRef = useRef(new WeakSet());

  useIsoLayoutEffect(
    () => () => {
      if (ctxRef.current) ctxRef.current.revert();
      ctxRef.current = null;
      seenRef.current = new WeakSet();
    },
    []
  );

  useIsoLayoutEffect(() => {
    const root = scopeRef.current;
    if (!root) return;
    const seen = seenRef.current;
    const fresh = Array.from(root.querySelectorAll(selector)).filter(
      (el) => !seen.has(el)
    );
    if (fresh.length === 0) return;
    fresh.forEach((el) => seen.add(el));
    if (prefersReducedMotion()) return;
    if (!ctxRef.current) ctxRef.current = gsap.context(() => {}, root);
    ctxRef.current.add(() => {
      gsap.fromTo(
        fresh.slice(0, max),
        { autoAlpha: 0, y },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          ease: 'power3.out',
          stagger,
          clearProps: 'opacity,visibility,transform',
        }
      );
    });
  });
}

/* Tactile press feedback through one delegated listener: anything inside
   `scopeRef` marked `data-press` squeezes on pointer-down and springs back
   on release. Presses that start on a nested button or link are left to
   that control's own feedback. */
export function usePressFeedback(scopeRef, selector = '[data-press]') {
  useEffect(() => {
    const root = scopeRef.current;
    if (!root || prefersReducedMotion()) return undefined;
    const touched = new Set();
    let active = null;

    const release = () => {
      if (!active) return;
      gsap.to(active, {
        scale: 1,
        duration: 0.55,
        ease: 'elastic.out(1, 0.5)',
        clearProps: 'transform',
        overwrite: true,
      });
      active = null;
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    };

    const onDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      const el = e.target.closest(selector);
      if (!el || !root.contains(el)) return;
      const control = e.target.closest(
        'button, a, input, textarea, select, [role="button"], [role="menuitem"]'
      );
      if (control && control !== el && el.contains(control)) return;
      active = el;
      touched.add(el);
      gsap.to(el, {
        scale: 0.975,
        duration: 0.14,
        ease: 'power2.out',
        overwrite: true,
      });
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
    };

    root.addEventListener('pointerdown', onDown);
    return () => {
      root.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      touched.forEach((el) => gsap.killTweensOf(el));
    };
  }, [scopeRef, selector]);
}

/* Smooth scroll to an element, instant when reduced motion is on. */
export function scrollToElement(el, offset = 88) {
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({
    top,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}
